/**
 * Organization API Service
 *
 * All calls to /api/organizations/* and /api/invitations/* go through
 * the standard apiClient which handles auth headers automatically.
 */

import { apiClient, postFormData } from './apiClient'
import type { LlmCredentialField } from '@archer/shared'

// ─── Types ───────────────────────────────────────────────────────────────────

export type OrgMemberRole = 'OWNER' | 'ADMIN' | 'MEMBER' | 'BILLING' | 'VIEWER'

export type InvitationStatus = 'PENDING' | 'ACCEPTED' | 'REVOKED' | 'EXPIRED'

export interface Organization {
  id: string
  name: string
  slug: string
  description?: string | null
  logoUrl?: string | null
  industry?: string | null
  website?: string | null
  billingEmail?: string | null
  status: string
  createdAt: string
  updatedAt?: string
  memberRole?: OrgMemberRole
  memberCount?: number
  workspaceCount?: number
  plan?: string
}

export interface OrgMember {
  id: string
  userId: string
  role: OrgMemberRole
  status: string
  joinedAt: string
  user: {
    id: string
    email: string
    displayName: string
    avatarUrl: string | null
  }
}

export interface OrgInvitation {
  id: string
  email: string
  role: OrgMemberRole
  status: string
  createdAt: string
  expiresAt: string
  inviter?: {
    id: string
    email: string
    displayName: string
  }
  /** Accept link for PENDING invitations — only returned to OWNER/ADMIN callers. */
  acceptUrl?: string
  /** Whether the invitation email actually went out (create/resend responses). */
  emailSent?: boolean
  /** Whether SMTP delivery is configured on this deployment (list responses). */
  emailConfigured?: boolean
}

export interface ResendInvitationResult {
  message: string
  expiresAt: string
  acceptUrl?: string
  emailSent?: boolean
}

export interface InvitationPreview {
  scopeType?: 'ORGANIZATION' | 'WORKSPACE'
  organizationName: string
  organizationSlug: string
  role?: OrgMemberRole
  workspaceId?: string
  workspaceName?: string
  accessRole?: 'ADMIN' | 'LEAD' | 'CONTRIBUTOR' | 'REVIEWER' | 'VIEWER'
  persona?: 'SPONSOR' | 'DELIVERY_LEAD' | 'ARCHITECT' | 'ENGINEER' | 'VALIDATOR' | 'BUSINESS_GOVERNANCE' | null
  specialtyTags?: string[]
  responsibilitySummary?: string | null
  inviterName: string
  expiresAt: string
  isExpired: boolean
  status: InvitationStatus
}

export interface InvitationAcceptResult {
  scopeType?: 'ORGANIZATION' | 'WORKSPACE'
  organization: Organization
  workspace?: {
    id: string
    name: string
  }
  membership: {
    role: OrgMemberRole | 'ADMIN' | 'LEAD' | 'CONTRIBUTOR' | 'REVIEWER' | 'VIEWER'
    joinedAt: string
    persona?: InvitationPreview['persona']
  }
}

export type OrgSecretType =
  | 'LLM_API_KEY'
  | 'CLOUD_CREDENTIAL'
  | 'INTEGRATION_TOKEN'
  | 'DATABASE_PASSWORD'
  | 'DATABASE_CREDENTIAL'
  | 'API_KEY'
  | 'CUSTOM'
  | 'GENERIC'

export interface OrgSecret {
  id: string
  key: string
  name?: string
  credentialField?: LlmCredentialField | null
  managedByLlmSetup?: boolean
  usedByLlmProfiles?: Array<{ id: string; provider: string }>
  secretType: OrgSecretType
  provider: string | null
  description: string | null
  maskedValue: string
  lastRotatedAt: string | null
  createdAt: string
  createdByUser: { id: string; email: string; displayName: string } | null
}

export interface AddSecretInput {
  key: string
  name?: string
  credentialField?: LlmCredentialField
  value: string
  secretType?: OrgSecretType
  provider?: string
  description?: string
}

export interface AuditLogActor {
  id: string
  email: string
  displayName: string | null
}

export type NormalizedAuditCategory = 'activity' | 'auth' | 'system'
export type NormalizedAuditTone = 'success' | 'error' | 'warning' | 'info' | 'default'

export interface NormalizedAuditEvent {
  id: string
  category: NormalizedAuditCategory
  action: string
  actionLabel: string
  occurredAt: string
  actor: AuditLogActor | null
  contextLabel: string
  contextSecondary?: string | null
  resultLabel: string
  resultTone: NormalizedAuditTone
  summary: string
  ipAddress: string | null
}

export interface NormalizedAuditPage {
  entries: NormalizedAuditEvent[]
  pagination: {
    page: number
    limit: number
    total: number
    hasMore: boolean
  }
}

export interface AuditLogFilters {
  page?: number
  limit?: number
  action?: string
  userId?: string
  resourceType?: string
  from?: string
  to?: string
}

export interface AuthAuditLogFilters {
  page?: number
  limit?: number
  action?: string
  provider?: string
  userId?: string
  success?: boolean
  from?: string
  to?: string
}

export interface SystemAuditLogFilters {
  page?: number
  limit?: number
  action?: string
  userId?: string
  resourceType?: string
  from?: string
  to?: string
}

export interface CreateOrgInput {
  name: string
  description?: string
  industry?: string
  website?: string
  slug?: string
}

export interface SendInvitationInput {
  email: string
  role: OrgMemberRole
  message?: string
}

export type StaticShareResource = 'WORKFLOW_GUIDE' | 'PMG_PLAYBOOK' | 'COMPLEXITY_IP_BRIEF'
export type ShareResource = StaticShareResource | 'ARTIFACT'
export type ShareLinkExpiryPreset = 'SHORT' | 'LONG' | 'NEVER'
export type ShareLinkStatus = 'ACTIVE' | 'EXPIRED' | 'REVOKED'

export interface ArtifactShareSummary {
  id: string
  artifactType: string
  title: string
  format: string
  version: number
  workspace: { id: string; name: string }
  storybook: { id: string; name: string }
}

export interface OrganizationShareLink {
  id: string
  resource: ShareResource
  label: string
  status: ShareLinkStatus
  expiresAt: string | null
  revokedAt: string | null
  accessCount: number
  lastAccessedAt: string | null
  createdAt: string
  creator: {
    id: string
    email: string
    displayName: string | null
  } | null
  artifact: ArtifactShareSummary | null
}

export interface CreatedOrganizationShareLink extends OrganizationShareLink {
  /** Returned only when the link is created. The server stores no recoverable token. */
  url: string
}

// ─── Organization API ─────────────────────────────────────────────────────────

export const organizationApi = {
  listMyOrgs: (): Promise<Organization[]> =>
    apiClient.get('/api/organizations').then((r: any) => r.data ?? r),

  createOrg: (data: CreateOrgInput): Promise<Organization> =>
    apiClient.post('/api/organizations', data).then((r: any) => r.data ?? r),

  getOrg: (orgId: string): Promise<Organization> =>
    apiClient.get(`/api/organizations/${orgId}`).then((r: any) => r.data ?? r),

  updateOrg: (orgId: string, data: Partial<CreateOrgInput>): Promise<Organization> =>
    apiClient.put(`/api/organizations/${orgId}`, data).then((r: any) => r.data ?? r),

  listMembers: (orgId: string): Promise<OrgMember[]> =>
    apiClient.get(`/api/organizations/${orgId}/members`).then((r: any) => r.data ?? r),

  updateMemberRole: (orgId: string, userId: string, role: OrgMemberRole): Promise<void> =>
    apiClient.put(`/api/organizations/${orgId}/members/${userId}`, { role }),

  removeMember: (orgId: string, userId: string): Promise<void> =>
    apiClient.delete(`/api/organizations/${orgId}/members/${userId}`),

  leaveOrg: (orgId: string): Promise<void> =>
    apiClient.delete(`/api/organizations/${orgId}/members/leave`),

  transferOwnership: (orgId: string, newOwnerId: string): Promise<void> =>
    apiClient.post(`/api/organizations/${orgId}/members/transfer-ownership`, { newOwnerId }),

  sendInvitation: (orgId: string, data: SendInvitationInput): Promise<OrgInvitation> =>
    apiClient.post(`/api/organizations/${orgId}/invitations`, data).then((r: any) => r.data ?? r),

  sendBulkInvitations: (
    orgId: string,
    invitations: SendInvitationInput[],
    message?: string
  ): Promise<OrgInvitation[]> =>
    apiClient
      .post(`/api/organizations/${orgId}/invitations`, { invitations, message })
      .then((r: any) => r.data ?? r),

  listInvitations: (orgId: string, status?: string): Promise<OrgInvitation[]> => {
    const url = status
      ? `/api/organizations/${orgId}/invitations?status=${status}`
      : `/api/organizations/${orgId}/invitations`
    return apiClient.get(url).then((r: any) => r.data ?? r)
  },

  revokeInvitation: (orgId: string, invId: string): Promise<void> =>
    apiClient.delete(`/api/organizations/${orgId}/invitations/${invId}`),

  resendInvitation: (orgId: string, invId: string): Promise<ResendInvitationResult> =>
    apiClient
      .post(`/api/organizations/${orgId}/invitations/${invId}/resend`, {})
      .then((r: any) => r.data ?? r),

  listShareLinks: (orgId: string): Promise<OrganizationShareLink[]> =>
    apiClient.get(`/api/organizations/${orgId}/share-links`).then((r: any) => r.data ?? r),

  createShareLink: (
    orgId: string,
    data: { resource: StaticShareResource; label: string; expiryPreset: ShareLinkExpiryPreset },
  ): Promise<CreatedOrganizationShareLink> =>
    apiClient.post(`/api/organizations/${orgId}/share-links`, data).then((r: any) => r.data ?? r),

  createArtifactShareLink: (
    artifactId: string,
    data: { label: string; expiryPreset: ShareLinkExpiryPreset },
  ): Promise<CreatedOrganizationShareLink> =>
    apiClient.post(`/api/artifacts/${artifactId}/share-links`, data).then((r: any) => r.data ?? r),

  revokeShareLink: (orgId: string, linkId: string): Promise<void> =>
    apiClient.delete(`/api/organizations/${orgId}/share-links/${linkId}`),

  listSecrets: (orgId: string): Promise<OrgSecret[]> =>
    apiClient.get(`/api/organizations/${orgId}/secrets`).then((r: any) => r.data ?? r),

  addSecret: (orgId: string, data: AddSecretInput): Promise<OrgSecret> =>
    apiClient.post(`/api/organizations/${orgId}/secrets`, data).then((r: any) => r.data ?? r),

  updateSecret: (orgId: string, secretId: string, data: { value?: string; name?: string; description?: string }): Promise<OrgSecret> =>
    apiClient.put(`/api/organizations/${orgId}/secrets/${secretId}`, data).then((r: any) => r.data ?? r),

  deleteSecret: (orgId: string, secretId: string): Promise<void> =>
    apiClient.delete(`/api/organizations/${orgId}/secrets/${secretId}`),

  getAuditLog: (orgId: string, filters?: AuditLogFilters): Promise<NormalizedAuditPage> => {
    const params = new URLSearchParams()
    if (filters?.page) params.set('page', String(filters.page))
    if (filters?.limit) params.set('limit', String(filters.limit))
    if (filters?.action) params.set('action', filters.action)
    if (filters?.userId) params.set('userId', filters.userId)
    if (filters?.resourceType) params.set('resourceType', filters.resourceType)
    if (filters?.from) params.set('from', filters.from)
    if (filters?.to) params.set('to', filters.to)
    const qs = params.toString()
    return apiClient
      .get('/api/organizations/' + orgId + '/audit-log' + (qs ? '?' + qs : ''))
      .then((r: any) => r.data ?? r)
  },

  getAuthAuditLog: (orgId: string, filters?: AuthAuditLogFilters): Promise<NormalizedAuditPage> => {
    const params = new URLSearchParams()
    if (filters?.page) params.set('page', String(filters.page))
    if (filters?.limit) params.set('limit', String(filters.limit))
    if (filters?.action) params.set('action', filters.action)
    if (filters?.provider) params.set('provider', filters.provider)
    if (filters?.userId) params.set('userId', filters.userId)
    if (typeof filters?.success === 'boolean') params.set('success', String(filters.success))
    if (filters?.from) params.set('from', filters.from)
    if (filters?.to) params.set('to', filters.to)
    const qs = params.toString()
    return apiClient
      .get('/api/organizations/' + orgId + '/auth-audit-log' + (qs ? '?' + qs : ''))
      .then((r: any) => r.data ?? r)
  },


  getSystemAuditLog: (orgId: string, filters?: SystemAuditLogFilters): Promise<NormalizedAuditPage> => {
    const params = new URLSearchParams()
    if (filters?.page) params.set('page', String(filters.page))
    if (filters?.limit) params.set('limit', String(filters.limit))
    if (filters?.action) params.set('action', filters.action)
    if (filters?.userId) params.set('userId', filters.userId)
    if (filters?.resourceType) params.set('resourceType', filters.resourceType)
    if (filters?.from) params.set('from', filters.from)
    if (filters?.to) params.set('to', filters.to)
    const qs = params.toString()
    return apiClient
      .get('/api/organizations/' + orgId + '/system-audit-log' + (qs ? '?' + qs : ''))
      .then((r: any) => r.data ?? r)
  },

  uploadAvatar: (orgId: string, file: File): Promise<Organization> => {
    const form = new FormData()
    form.append('avatar', file)
    return postFormData<{ success: boolean; data: Organization }>(
      `/api/organizations/${orgId}/avatar`,
      form
    ).then((r) => r.data)
  },

  removeAvatar: (orgId: string): Promise<Organization> =>
    apiClient.delete(`/api/organizations/${orgId}/avatar`).then((r: any) => r.data ?? r),

  getInvitationByToken: (token: string): Promise<InvitationPreview> =>
    apiClient.get(`/api/invitations/${token}`).then((r: any) => r.data ?? r),

  acceptInvitation: (token: string): Promise<InvitationAcceptResult> =>
    apiClient.post(`/api/invitations/${token}/accept`, {}).then((r: any) => r.data ?? r),

  isBootstrapAdmin: (email: string): Promise<boolean> =>
    apiClient
      .get(`/api/provisioning/is-bootstrap-admin?email=${encodeURIComponent(email)}`)
      .then((r: any) => r.data?.isBootstrapAdmin ?? false),

  getProvisioningPolicy: (): Promise<{ mode: string; allowedDomains?: string[]; requireDomainVerification?: boolean; allowPersonalEmails?: boolean }> =>
    apiClient.get('/api/provisioning/policy').then((r: any) => r.data ?? r),
}
